import React, { createContext, useContext, useState, useEffect } from "react";
import { logUserLogin } from "../../firebase/logsService";

export interface User {
  uid: string;
  email: string | null;
  name?: string;
  birth_date?: string;
  [key: string]: any;
}

// Fonction pour obtenir l'adresse IP du client
const getClientIP = async (): Promise<string> => {
  try {
    // Essayer d'obtenir l'IP publique via plusieurs services
    const services = [
      "https://api.ipify.org?format=json",
      "https://ipapi.co/json/",
      "https://api.my-ip.io/ip.json",
    ];

    for (const service of services) {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000);

        const response = await fetch(service, {
          signal: controller.signal,
          headers: {
            Accept: "application/json",
          },
        });

        clearTimeout(timeoutId);

        if (response.ok) {
          const data = await response.json();

          // Extraire l'IP selon le format de réponse du service
          let ip = data.ip || data.query || data.IPv4;

          if (
            ip &&
            ip !== "127.0.0.1" &&
            !ip.startsWith("192.168.") &&
            !ip.startsWith("10.")
          ) {
            return ip;
          }
        }
      } catch (serviceError) {
        console.warn(`Service ${service} failed:`, serviceError);
        continue;
      }
    }

    // Si aucun service ne fonctionne, générer une IP aléatoire pour la démo
    const randomIP = `${Math.floor(Math.random() * 255)}.${Math.floor(Math.random() * 255)}.${Math.floor(Math.random() * 255)}.${Math.floor(Math.random() * 255)}`;
    return randomIP;
  } catch (error) {
    console.error("Erreur lors de la récupération de l'IP:", error);
    // Générer une IP aléatoire pour la démo
    const randomIP = `${Math.floor(Math.random() * 255)}.${Math.floor(Math.random() * 255)}.${Math.floor(Math.random() * 255)}.${Math.floor(Math.random() * 255)}`;
    return randomIP;
  }
};

interface AuthContextType {
  userName?: string;
  currentUser: User | null;
  signup: (email: string, password: string, name: string) => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  confirmPasswordReset: (
    token: string,
    new_password: string,
    confirm_password: string,
  ) => Promise<void>;
  updateProfile: (data: {
    first_name?: string;
    last_name?: string;
    name?: string;
    birth_date?: string;
  }) => Promise<void>;
  dispatchUserName: (name: string) => void;
  loadingProfile: boolean;
  dispatchProfileLoading: (state: boolean) => void;
}

const AuthContext = createContext<AuthContextType>({
  userName: "",
  currentUser: null,
  signup: async () => {},
  login: async () => {},
  logout: async () => {},
  resetPassword: async () => {},
  confirmPasswordReset: async () => {},
  updateProfile: async () => {},
  dispatchUserName: () => {},
  loadingProfile: false,
  dispatchProfileLoading: () => {},
});

// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = () => useContext(AuthContext);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [userName, setUserName] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadingProfile, dispatchProfileLoading] = useState(false);

  const API_BASE = import.meta.env.VITE_API_BASE || "https://apicrdb.ssa.tg";

  const signup = async (email: string, password: string, name: string) => {
    try {
      const nameParts = name.trim().split(" ");
      const first_name = nameParts[0] || "User";
      const last_name = nameParts.slice(1).join(" ") || "Unknown";

      const response = await fetch(`${API_BASE}/api/v1/auth/register/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email,
          password,
          first_name,
          last_name,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error("API Error Data:", errorData);
        const errorMsg =
          errorData.detail || errorData.message || JSON.stringify(errorData);
        throw new Error(errorMsg || "Échec de l'inscription");
      }

      // Auto login après inscription
      await login(email, password);
    } catch (error) {
      console.error("Error during signup:", error);
      throw error;
    }
  };

  const login = async (email: string, password: string) => {
    const response = await fetch(`${API_BASE}/api/v1/auth/login/`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        email,
        password,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || "Échec de l'authentification");
    }

    const data = await response.json();

    // Check various common paths for the token
    const token =
      data.result?.tokens?.access ||
      data.result?.access ||
      data.tokens?.access ||
      data.access ||
      data.token ||
      data.access_token;

    if (token) {
      localStorage.setItem("auth_token", token);
    } else {
      console.warn("No token found in login response", data);
    }

    const userData = data.result?.user || data.user || data.result || {};
    const userObj: User = {
      uid: userData.id || userData.uid || userData.uuid || email,
      email: userData.email || email,
      name: userData.name || userData.first_name || "",
    };

    setCurrentUser(userObj);

    // Logging IP and Login event
    try {
      const clientIP = await getClientIP();
      await logUserLogin(userObj.email || "", clientIP);
    } catch (error) {
      console.error("Erreur log", error);
    }
  };

  const logout = async () => {
    localStorage.removeItem("auth_token");
    setCurrentUser(null);
  };

  const resetPassword = async (email: string) => {
    const response = await fetch(`${API_BASE}/api/v1/auth/password/reset/`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ email }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const errorMsg =
        errorData.detail || errorData.message || JSON.stringify(errorData);
      throw new Error(errorMsg || "Échec de la réinitialisation");
    }
  };

  const confirmPasswordReset = async (
    token: string,
    new_password: string,
    confirm_password: string,
  ) => {
    const response = await fetch(
      `${API_BASE}/api/v1/auth/password/reset/confirm/`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ token, new_password, confirm_password }),
      },
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const errorMsg =
        errorData.detail || errorData.message || JSON.stringify(errorData);
      throw new Error(
        errorMsg || "Échec de la confirmation de réinitialisation",
      );
    }
  };

  const updateProfile = async (profileData: {
    first_name?: string;
    last_name?: string;
    name?: string;
    birth_date?: string;
  }) => {
    const token = localStorage.getItem("auth_token");
    if (!token) throw new Error("Non authentifié");

    const response = await fetch(`${API_BASE}/api/v1/auth/me/`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(profileData),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.detail ||
          errorData.message ||
          "Échec de la mise à jour du profil",
      );
    }

    const data = await response.json();
    const userData = data.result || data;

    // Mettre à jour le state local
    setCurrentUser((prev) => {
      if (!prev) return null;
      return {
        ...prev,
        name: userData.name || userData.first_name || prev.name,
        birth_date: userData.birth_date || prev.birth_date,
      };
    });
  };

  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem("auth_token");
      if (token) {
        try {
          const res = await fetch(`${API_BASE}/api/v1/auth/me/`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });

          if (res.ok) {
            const data = await res.json();
            const userData = data.result || data; // fallback to data if result is missing
            setCurrentUser({
              uid: userData.uuid || userData.id || userData.uid || "user-id",
              email: userData.email,
              name: userData.name || userData.first_name,
              birth_date: userData.birth_date,
            });
          } else {
            localStorage.removeItem("auth_token");
            setCurrentUser(null);
          }
        } catch (err) {
          console.error("Erreur vérification token:", err);
          localStorage.removeItem("auth_token");
          setCurrentUser(null);
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, []);

  const value = {
    currentUser,
    userName,
    signup,
    login,
    logout,
    resetPassword,
    confirmPasswordReset,
    updateProfile,
    dispatchUserName: setUserName,
    loadingProfile,
    dispatchProfileLoading,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
